import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api, type ApiError } from '@/lib/api';
import { toast } from '@/lib/toast';
import type {
  ClientLink,
  ClientMap,
  DismissedRow,
  Invoice,
  InvoiceDetail,
  ItflowSettings,
  ItflowSettingsInput,
  ProjectMap,
  ReadyRow,
  SyncResult,
  TestResult,
} from './types';

const SETTINGS = ['itflow', 'settings'] as const;
const CLIENTS = ['itflow', 'clients'] as const;
const READY = ['itflow', 'ready'] as const;
const DISMISSED = ['itflow', 'dismissed'] as const;
const INVOICES = ['itflow', 'invoices'] as const;

const projectsKey = (clientId: number) => ['itflow', 'projects', clientId] as const;
const invoiceKey = (id: number) => ['itflow', 'invoices', id] as const;

/** Everything the mapping and billing screens depend on. */
function invalidateAll(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ['itflow'] });
}

// Settings

export function useItflowSettings() {
  return useQuery<ItflowSettings>({
    queryKey: SETTINGS,
    queryFn: () => api<ItflowSettings>('/api/itflow/settings'),
  });
}

export function useSaveItflowSettings() {
  const qc = useQueryClient();
  return useMutation<ItflowSettings, ApiError, ItflowSettingsInput>({
    mutationFn: (body) =>
      api<ItflowSettings>('/api/itflow/settings', { method: 'PUT', json: body }),
    onSuccess: (data) => {
      qc.setQueryData(SETTINGS, data);
      invalidateAll(qc);
    },
    onError: (error) => toast.error(error.message),
  });
}

export function useDeleteItflowSettings() {
  const qc = useQueryClient();
  return useMutation<void, ApiError, void>({
    mutationFn: () => api<void>('/api/itflow/settings', { method: 'DELETE' }),
    onSuccess: () => invalidateAll(qc),
    onError: (error) => toast.error(error.message),
  });
}

export function useTestItflow() {
  const qc = useQueryClient();
  return useMutation<TestResult, ApiError, void>({
    mutationFn: () => api<TestResult>('/api/itflow/settings/test', { method: 'POST' }),
    onSuccess: (data) => {
      if (data.ok) toast.show(data.message);
      else toast.error(data.message);
      qc.invalidateQueries({ queryKey: SETTINGS });
    },
    onError: (error) => toast.error(error.message),
  });
}

// Client mapping

export function useClientMap(enabled: boolean) {
  return useQuery<ClientMap>({
    queryKey: CLIENTS,
    queryFn: () => api<ClientMap>('/api/itflow/clients'),
    enabled,
  });
}

export function useLinkClient() {
  const qc = useQueryClient();
  return useMutation<ClientLink, ApiError, { client_id: number; itflow_client_id: number }>({
    mutationFn: (body) =>
      api<ClientLink>('/api/itflow/clients/link', { method: 'POST', json: body }),
    onSuccess: () => {
      toast.show('Client mapped');
      invalidateAll(qc);
    },
    onError: (error) => toast.error(error.message),
  });
}

export function useUnlinkClient() {
  const qc = useQueryClient();
  return useMutation<void, ApiError, number>({
    mutationFn: (linkId) =>
      api<void>(`/api/itflow/clients/link/${linkId}`, { method: 'DELETE' }),
    onSuccess: () => {
      toast.show('Mapping removed. Past invoices are unaffected.');
      invalidateAll(qc);
    },
    onError: (error) => toast.error(error.message),
  });
}

export function usePushClient() {
  const qc = useQueryClient();
  return useMutation<ClientLink, ApiError, number>({
    mutationFn: (clientId) =>
      api<ClientLink>('/api/itflow/clients/push', {
        method: 'POST',
        json: { client_id: clientId },
      }),
    onSuccess: () => {
      toast.show('Created in ITFlow and mapped');
      invalidateAll(qc);
    },
    onError: (error) => toast.error(error.message),
  });
}

export function usePullClient() {
  const qc = useQueryClient();
  return useMutation<ClientLink, ApiError, number>({
    mutationFn: (itflowClientId) =>
      api<ClientLink>('/api/itflow/clients/pull', {
        method: 'POST',
        json: { itflow_client_id: itflowClientId },
      }),
    onSuccess: () => {
      toast.show('Created in Chronos and mapped');
      invalidateAll(qc);
      qc.invalidateQueries({ queryKey: ['clients'] });
    },
    onError: (error) => toast.error(error.message),
  });
}

// Project mapping

export function useProjectMap(clientId: number | null) {
  return useQuery<ProjectMap>({
    queryKey: projectsKey(clientId ?? 0),
    queryFn: () => api<ProjectMap>(`/api/itflow/projects?client_id=${clientId}`),
    enabled: clientId !== null,
  });
}

export function useSyncProjects() {
  const qc = useQueryClient();
  return useMutation<SyncResult, ApiError, number>({
    mutationFn: (clientId) =>
      api<SyncResult>('/api/itflow/projects/sync', {
        method: 'POST',
        json: { client_id: clientId },
      }),
    onSuccess: (result) => {
      const parts = [
        result.pushed && `${result.pushed} pushed`,
        result.pulled && `${result.pulled} pulled`,
        result.linked && `${result.linked} linked`,
      ].filter(Boolean);
      toast.show(parts.length ? `Projects synced: ${parts.join(', ')}` : 'Already in sync');
      invalidateAll(qc);
      qc.invalidateQueries({ queryKey: ['clients'] });
    },
    onError: (error) => toast.error(error.message),
  });
}

// Ready to bill

export function useReadyToBill(clientId: number | null, enabled: boolean) {
  return useQuery<ReadyRow[]>({
    queryKey: [...READY, clientId] as const,
    queryFn: () =>
      api<ReadyRow[]>(
        clientId === null
          ? '/api/itflow/ready-to-bill'
          : `/api/itflow/ready-to-bill?client_id=${clientId}`,
      ),
    enabled,
  });
}

export function useDismissed(enabled: boolean) {
  return useQuery<DismissedRow[]>({
    queryKey: DISMISSED,
    queryFn: () => api<DismissedRow[]>('/api/itflow/dismissed'),
    enabled,
  });
}

export function useDismissTasks() {
  const qc = useQueryClient();
  const undismiss = useUndismissTasks();
  return useMutation<void, ApiError, number[]>({
    mutationFn: (taskIds) =>
      api<void>('/api/itflow/ready-to-bill/dismiss', {
        method: 'POST',
        json: { task_ids: taskIds },
      }),
    onSuccess: (_data, taskIds) => {
      // Semi-destructive, so it gets undo rather than a confirmation prompt.
      toast.undoable(
        `${taskIds.length} task${taskIds.length === 1 ? '' : 's'} marked never-bill`,
        () => undismiss.mutate(taskIds),
      );
      invalidateAll(qc);
    },
    onError: (error) => toast.error(error.message),
  });
}

export function useUndismissTasks() {
  const qc = useQueryClient();
  return useMutation<void, ApiError, number[]>({
    mutationFn: (taskIds) =>
      api<void>('/api/itflow/ready-to-bill/undismiss', {
        method: 'POST',
        json: { task_ids: taskIds },
      }),
    onSuccess: () => invalidateAll(qc),
    onError: (error) => toast.error(error.message),
  });
}

// Invoices

export function useInvoices(enabled: boolean) {
  return useQuery<Invoice[]>({
    queryKey: INVOICES,
    queryFn: () => api<Invoice[]>('/api/itflow/invoices'),
    enabled,
    // Generation happens in a worker, so the list is how progress is seen.
    refetchInterval: (query) =>
      (query.state.data ?? []).some(
        (i) => i.state === 'pending' || i.state === 'invoice_created',
      )
        ? 3000
        : false,
  });
}

export function useInvoice(id: number | null) {
  return useQuery<InvoiceDetail>({
    queryKey: invoiceKey(id ?? 0),
    queryFn: () => api<InvoiceDetail>(`/api/itflow/invoices/${id}`),
    enabled: id !== null,
  });
}

export interface GenerateInput {
  client_id: number;
  task_ids: number[];
  /** Minted once per press and reused on retry -- this is the idempotency key. */
  request_key: string;
  scope?: string;
}

export function useGenerateInvoice() {
  const qc = useQueryClient();
  return useMutation<Invoice, ApiError, GenerateInput>({
    mutationFn: (body) =>
      api<Invoice>('/api/itflow/invoices', { method: 'POST', json: body }),
    onSuccess: () => {
      toast.show('Invoice queued: lines post in the background');
      invalidateAll(qc);
    },
    onError: (error) => toast.error(error.message),
  });
}

export function useRetryInvoice() {
  const qc = useQueryClient();
  return useMutation<Invoice, ApiError, number>({
    mutationFn: (id) =>
      api<Invoice>(`/api/itflow/invoices/${id}/retry`, { method: 'POST' }),
    onSuccess: () => {
      toast.show('Retrying: only the missing lines are posted');
      invalidateAll(qc);
    },
    onError: (error) => toast.error(error.message),
  });
}
